import React, { useEffect, useState, useCallback } from 'react';
import { ShieldAlert, ShieldCheck } from 'lucide-react';
import { getBatteryOptStatus } from '../../lib/batteryOptStatus';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Persistent pill shown on the staff dashboard (across every tab) so admins
 * supporting a staff member can immediately tell whether their phone has
 * battery optimisation disabled for Carloo Tex.
 *
 * Behaviour:
 *  • On a native (Capacitor) Android build: queries the BatteryOptPlugin and
 *    shows red 🛡️ "Battery saver ON" or green ✓ "Battery saver OFF".
 *  • On web / desktop: hidden (the OS-level optimiser doesn't apply).
 *  • Re-checks every 30 s and on tab visibility-change.
 *  • Pushes the latest status to the backend (`/api/staff/battery-opt`) so
 *    admins can see it in the Daily Report.
 *
 * Click → re-opens the BatterySettingsWizard so staff can fix it on the spot.
 *
 * Props:
 *   onOpenWizard: () => void   — called when staff taps the pill
 */
export default function BatteryOptIndicator({ onOpenWizard }) {
  const [status, setStatus] = useState(null);

  const check = useCallback(async (push = true) => {
    const r = await getBatteryOptStatus();
    setStatus(r);
    if (push && r.native) {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          await fetch(`${API_URL}/api/staff/battery-opt`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({
              ignored: r.ignored,
              manufacturer: r.manufacturer,
              model: r.model,
              sdk: r.sdk,
            }),
          });
        }
      } catch (_) {}
    }
  }, []);

  useEffect(() => {
    check();
    const interval = setInterval(() => check(), 30_000);
    const onVis = () => { if (!document.hidden) check(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(interval); document.removeEventListener('visibilitychange', onVis); };
  }, [check]);

  if (!status || !status.native) return null;
  if (status.ignored === null) return null;

  const ok = status.ignored;
  return (
    <button
      onClick={onOpenWizard}
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold border ${
        ok
          ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
          : 'bg-red-50 text-red-700 border-red-300 hover:bg-red-100 animate-pulse'
      }`}
      title={ok ? 'Battery optimisation is disabled for Carloo Tex — good.' : 'Battery optimisation is still ON — phone may kill GPS. Tap to fix.'}
      data-testid="battery-opt-pill"
    >
      {ok ? <ShieldCheck size={11} /> : <ShieldAlert size={11} />}
      {ok ? 'Battery OK' : 'Battery saver ON'}
    </button>
  );
}
